import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Handlers {
  onShowHelp: () => void;
}

/**
 * Centralised global-keyboard-shortcut handler.
 *
 * Listens at the window level for:
 *   - `g <key>` two-key navigation chords (g p / g t / g k / g c / g m / g a)
 *   - single-key actions (? → help, / → focus search)
 *
 * Quick-create lives in the Cmd-K command palette ("New project", etc.).
 *
 * Suppresses itself while focus is inside an input / textarea / contenteditable
 * so it never swallows typing. Cmd-K is handled separately by the
 * CommandPalette component (which lives lower in the tree).
 *
 * Returns nothing — purely a side-effect hook mounted from Layout.
 */
export function useKeyboardShortcuts(handlers: Handlers): void {
  const navigate = useNavigate();
  const lastG = useRef<number>(0);

  useEffect(() => {
    const isEditable = (el: EventTarget | null): boolean => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      // MUI's Dialog backdrop captures focus inside the dialog; their inputs
      // already match INPUT/TEXTAREA so we're covered.
      return false;
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;

      const key = e.key;

      // Two-key navigation: g <x>
      const now = Date.now();
      if (key === 'g') {
        lastG.current = now;
        return;
      }
      if (lastG.current && now - lastG.current < 1200) {
        lastG.current = 0;
        const dest: Record<string, string> = {
          p: '/projects',
          t: '/tasks',
          k: '/tickets',
          c: '/clients',
          m: '/my-assignments',
          a: '/calendar'
        };
        if (dest[key]) {
          e.preventDefault();
          navigate(dest[key]);
          return;
        }
      }

      // Single-key actions
      if (key === '?') {
        e.preventDefault();
        handlers.onShowHelp();
        return;
      }
      if (key === '/') {
        e.preventDefault();
        const input = document.querySelector<HTMLInputElement>('input[placeholder^="Search"]');
        input?.focus();
        return;
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, handlers]);
}
